import React from 'react';
import {
	Button,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Avatar,
	Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';

import SectionEditor from './SectionEditor';

const SectionList = ({ template, setTemplate }) => {
	const handleAddSection = () => {
		const updatedTemplate = {
			...template,
			sections: [
				...template.sections,
				{
					id: template.sections.length + 1,
					name: 'New Section',
					type: 'text',
					elements: [],
				},
			],
		};
		setTemplate(updatedTemplate);
	};

	const handleDeleteSection = (index) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.filter((section, i) => i !== index),
		};
		setTemplate(updatedTemplate);
	};

	const handleUpdateSectionName = (index, name) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.map((section, i) => {
				if (i === index) {
					return {
						...section,
						name: name,
					};
				} else {
					return section;
				}
			}),
		};
		setTemplate(updatedTemplate);
	};

	const handleDragEnd = (result) => {
		if (!result.destination) {
			return;
		}
		const { source, destination } = result;
		const updatedSections = [...template.sections];
		const [removed] = updatedSections.splice(source.index, 1);
		updatedSections.splice(destination.index, 0, removed);
		const updatedTemplate = {
			...template,
			sections: updatedSections,
		};
		setTemplate(updatedTemplate);
	};

	const handleUpdateSectionVersion = (index, version) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.map((section, i) => {
				if (i === index) {
					return {
						...section,
						version: version,
					};
				} else {
					return section;
				}
			}),
		};
		setTemplate(updatedTemplate);
	};

	const handleUpdateSectionType = (index, type) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.map((section, i) => {
				if (i === index) {
					return {
						...section,
						type: type,
					};
				} else {
					return section;
				}
			}),
		};
		setTemplate(updatedTemplate);
	};

	const handleAddSectionElement = (index) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.map((section, i) => {
				if (i === index) {
					return {
						...section,
						elements: [
							...section.elements,
							{
								id: uuidv4(),
								label: '',
							},
						],
					};
				} else {
					return section;
				}
			}),
		};
		setTemplate(updatedTemplate);
	};

	const handleDeleteSectionElement = (sectionIndex, elementIndex) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.map((section, i) => {
				if (i === sectionIndex) {
					return {
						...section,
						elements: section.elements.filter((element, j) => j !== elementIndex),
					};
				} else {
					return section;
				}
			}),
		};
		setTemplate(updatedTemplate);
	};

	const handleUpdateSectionElement = (sectionIndex, elementIndex, value) => {
		const updatedTemplate = {
			...template,
			sections: template.sections.map((section, i) => {
				if (i === sectionIndex) {
					return {
						...section,
						elements: section.elements.map((element, j) => {
							if (j === elementIndex) {
								return {
									...element,
									label: value,
								};
							} else {
								return element;
							}
						}),
					};
				} else {
					return section;
				}
			}),
		};
		setTemplate(updatedTemplate);
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="h6">Sections</Typography>
			</Grid>
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="sections">
					{(provided) => (
						<Grid item xs={12} {...provided.droppableProps} ref={provided.innerRef}>
							{template.sections.map((section, index) => (
								<Draggable
									key={index}
									draggableId={`section-${index}`}
									index={index}>
									{(provided) => (
										<div {...provided.draggableProps} ref={provided.innerRef}>
											<Accordion
												sx={{ marginBottom: '8px', marginTop: '8px' }}>
												<AccordionSummary
													expandIcon={<ExpandMoreIcon />}
													aria-controls={`section-${index}-content`}
													id={`section-${index}-header`}
													{...provided.dragHandleProps}>
													<Box display="flex">
														<DragIndicatorIcon />
														<Typography>{section.name}</Typography>
													</Box>
												</AccordionSummary>
												<AccordionDetails>
													<SectionEditor
														index={index}
														section={section}
														sectionIndex={index}
														handleUpdateSectionName={
															handleUpdateSectionName
														}
														handleUpdateSectionVersion={
															handleUpdateSectionVersion
														}
														handleUpdateSectionType={
															handleUpdateSectionType
														}
														handleAddSectionElement={
															handleAddSectionElement
														}
														handleDeleteSectionElement={
															handleDeleteSectionElement
														}
														handleUpdateSectionElement={
															handleUpdateSectionElement
														}
													/>
													<Button
														color="error"
														onClick={() => handleDeleteSection(index)}>
														Delete Section
													</Button>
												</AccordionDetails>
											</Accordion>
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</Grid>
					)}
				</Droppable>
			</DragDropContext>
			<Grid item xs={12}>
				<Button variant="contained" color="primary" onClick={handleAddSection}>
					Add Section
				</Button>
			</Grid>
		</Grid>
	);
};

export default SectionList;
